import { FC, useContext } from "react";

import { CrossIcon } from "../../../assets/img/icons";
import NavItem from "../NavItem";
import NavDivider from "../NavDivider";
import { AuthContext } from "../../../contexts/AuthContext";
import { Resource } from "../../../types/acl";

import "./style.css";

export interface Link {
    to?: string;
    label: string;
    icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    resource?: Resource | Resource[];
    subItems?: Link[];
    className?: string;
}

interface Props {
    navItems: Link[];
    showSubItemDividers?: boolean;
    onClose?: () => void;
    closeOnSectionLabel?: string;
    closeButtonClassName?: string;
    sectionHeaderClassName?: string;
}

const Nav: FC<Props> = ({
    navItems,
    showSubItemDividers = false,
    onClose,
    closeOnSectionLabel,
    closeButtonClassName,
    sectionHeaderClassName,
}) => {
    const { hasResource } = useContext(AuthContext)!;
    const hasResourceAccess = (resource?: Resource | Resource[]) => {
        if (resource === undefined) return true;
        return Array.isArray(resource)
            ? resource.some((value) => hasResource(value))
            : hasResource(resource);
    };

    const hasVisibleDescendant = (link: Link): boolean => {
        if (link.subItems?.some((sub) => hasVisibleDescendant(sub))) {
            return true;
        }
        // Feuille cliquable : besoin d'une route + permission
        if (link.to) {
            return hasResourceAccess(link.resource);
        }
        // Groupe sans lien : visible seulement via descendants
        return false;
    };

    const renderLinks = (
        links: Link[],
        showDivider: boolean
    ): React.ReactNode =>
        links.map(
            ({ to, label, icon: Icon, resource, subItems, className }, index) => {
                const isGroupOnly = !to && Boolean(subItems?.length);
                const hasDirectAccess = hasResourceAccess(resource);
                const hasVisibleSubItems = Boolean(
                    subItems?.some((subItem) => hasVisibleDescendant(subItem))
                );
                const canRenderItem = isGroupOnly
                    ? hasVisibleSubItems
                    : hasDirectAccess || hasVisibleSubItems;
                const showCloseButton =
                    Boolean(onClose) &&
                    Boolean(subItems?.length) &&
                    label === closeOnSectionLabel;

                return canRenderItem ? (
                    <div key={`${to || label}-${index}`}>
                        {showDivider && index !== 0 && <NavDivider />}
                        {showCloseButton ? (
                            <div className={sectionHeaderClassName}>
                                <button
                                    type="button"
                                    className={closeButtonClassName}
                                    onClick={onClose}
                                    aria-label="Fermer le menu"
                                >
                                    <CrossIcon color="#A3A9C2" width={18} height={18} />
                                </button>
                                <NavItem
                                    to={to}
                                    label={label}
                                    Icon={Icon}
                                    className={className}
                                />
                            </div>
                        ) : (
                            <NavItem
                                to={to}
                                label={label}
                                Icon={Icon}
                                className={className}
                            />
                        )}
                        {subItems && subItems.length > 0 && (
                            <div className="nav_subItems-container">
                                {renderLinks(subItems, showSubItemDividers)}
                            </div>
                        )}
                    </div>
                ) : null;
            }
        );

    return <div>{renderLinks(navItems, true)}</div>;
};

export default Nav;
